import React from 'react';

import { Flex, Text, TextProps } from '@indeed/ifl-components';

import { RadioCardCommonProps, RadioCardInputProps, RadioCardVariants } from './ResumeRadioCard';
import { RadioCardGroupProvider } from './RadioCardGroupContext';

export interface RadioCardGroupProps
    extends RadioCardCommonProps,
        Omit<RadioCardInputProps, 'value'> {
    /**
     * Radio card group legend
     */
    legend?: React.ReactNode;
    /**
     * Props to pass to the legend element
     */
    legendProps?: Omit<TextProps, 'as'>;
    /**
     * Name of the radio card group
     */
    name: string;
    /**
     * Value of the selected radio card
     */
    selectedValue?: string;
    /**
     * Variant of RadioCard elements in the group
     */
    variant?: RadioCardVariants;
}

export const RadioCardGroup = React.forwardRef<
    HTMLFieldSetElement,
    React.PropsWithChildren<RadioCardGroupProps>
>(
    (
        {
            children,
            legend,
            legendProps,
            name: nameProp,
            onChange,
            selectedValue,
            sx,
            variant,
            'data-testid': dataTestId = 'radio-card-group',
            ...props
        },
        ref,
    ) => {
        const name = nameProp;

        return (
            <RadioCardGroupProvider value={{ name, onChange, selectedValue, variant }}>
                <Flex
                    as="fieldset"
                    data-testid={dataTestId}
                    ref={ref}
                    role="radiogroup"
                    sx={{ border: 0, flexDirection: 'column', p: 0, rowGap: 3, ...sx }}
                    tabIndex={-1}
                    {...props}
                >
                    {!!legend && (
                        <Text
                            data-testid={`${dataTestId}-legend`}
                            level={3}
                            variant="contrast"
                            {...legendProps}
                            as="legend"
                            sx={{ fontWeight: 'bold', marginBlockEnd: 3, p: 0, ...legendProps?.sx }}
                        >
                            {legend}
                        </Text>
                    )}
                    {children}
                </Flex>
            </RadioCardGroupProvider>
        );
    },
);
